import React from 'react';
import { Check, Loader2 } from 'lucide-react';
import { getPhaseSteps } from './docIntelUtils';

export const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#234952] focus:outline-none focus:ring-2 focus:ring-[#2A565E]/30 disabled:cursor-not-allowed disabled:opacity-50';

export const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';

export const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50';

export function DocIntelMainCard({ children, className = '' }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.03] ${className}`}>
      {children}
    </div>
  );
}

/** Compact phase rail — not a clickable stepper */
export function DocIntelPhaseRail({ panel, includeTemplate = false, mode = 'deal' }) {
  const steps = getPhaseSteps(panel, { includeTemplate, mode });
  if (panel === 'empty') return null;

  return (
    <div
      className="border-b border-slate-100 bg-gradient-to-r from-teal-50/50 via-white to-slate-50/80 px-4 py-3 sm:px-5"
      aria-label="Document processing progress">
      <ol className="flex flex-wrap items-center gap-1 sm:gap-0">
        {steps.map((step, index) => (
          <li key={step.id} className="flex items-center">
            <div
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition sm:px-3 ${
                step.state === 'active'
                  ? 'bg-[#2A565E] text-white shadow-sm'
                  : step.state === 'done'
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'text-slate-400'
              }`}>
              {step.state === 'done' ? (
                <Check className="h-3 w-3 shrink-0" strokeWidth={2.5} />
              ) : step.state === 'active' && panel === 'processing' ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
              ) : (
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                    step.state === 'active' ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                  {index + 1}
                </span>
              )}
              <span className="hidden text-sm font-medium sm:inline">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`mx-1 hidden h-px w-6 sm:block md:w-10 ${
                  step.state === 'done' ? 'bg-emerald-200' : 'bg-slate-200'
                }`}
              />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function DocIntelPanelHeader({ title, subtitle, hint, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-5">
      <div className="min-w-0">
        <h3 className="text-base font-semibold tracking-tight text-slate-900">{title}</h3>
        {subtitle && <p className="mt-0.5 truncate text-sm text-slate-500">{subtitle}</p>}
        {hint && <p className="mt-1.5 text-xs text-slate-400">{hint}</p>}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function ConfidenceBar({ confidence, className = '' }) {
  const pct = Math.round(Math.min(1, Math.max(0, Number(confidence ?? 1))) * 100);
  const barColor =
    pct < 75 ? 'bg-red-400' : pct < 85 ? 'bg-amber-400' : 'bg-emerald-400';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`w-9 text-right text-xs font-bold tabular-nums ${pct < 75 ? 'text-red-600' : pct < 85 ? 'text-amber-700' : 'text-slate-600'}`}>
        {pct}%
      </span>
    </div>
  );
}

export function SourceBadge({ source }) {
  const isLlm = source === 'llm';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        isLlm ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'
      }`}>
      {isLlm ? 'AI' : source || 'rules'}
    </span>
  );
}

export function DocIntelStatePanel({ tone = 'neutral', icon: Icon, title, description, action, embedded = false, spinIcon = false }) {
  const tones = {
    neutral: 'border-slate-200 bg-slate-50/50',
    processing: 'border-teal-200/80 bg-gradient-to-br from-teal-50/80 via-white to-slate-50',
    error: 'border-red-200/80 bg-red-50/40',
    success: 'border-emerald-200/80 bg-emerald-50/30'
  };

  const inner = (
    <div className={`flex min-h-[300px] flex-col items-center justify-center px-6 py-14 text-center ${embedded ? '' : tones[tone]}`}>
      {Icon && (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80">
          <Icon className={`h-7 w-7 ${spinIcon ? 'animate-spin' : ''} ${tone === 'error' ? 'text-red-600' : tone === 'success' ? 'text-emerald-600' : 'text-teal-700'}`} />
        </div>
      )}
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {description && (
        <p className={`mt-2 max-w-md text-sm ${tone === 'error' ? 'text-red-800' : 'text-slate-600'}`}>
          {description}
        </p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );

  if (embedded) return inner;
  return <DocIntelMainCard>{inner}</DocIntelMainCard>;
}

export function DocIntelSkeleton({ rows = 4, className = '' }) {
  return (
    <div className={`animate-pulse space-y-4 p-4 sm:p-5 ${className}`} aria-hidden="true">
      <div className="h-4 w-1/3 rounded bg-slate-200" />
      <div className="h-3 w-1/2 rounded bg-slate-100" />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-slate-100 bg-slate-50/50 p-4">
          <div className="h-3 w-1/4 rounded bg-slate-200" />
          <div className="h-9 w-full rounded-lg bg-slate-200/80" />
          <div className="h-1.5 w-full rounded-full bg-slate-100" />
        </div>
      ))}
    </div>
  );
}
